import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTeacher, getCourses, toggleFavoriteTeacher } from '../services/api';
import api from '../services/api';
import CourseCard from '../components/CourseCard';
import { Skeleton } from '../components/Skeleton';
import { useScrollReveal } from '../hooks/useScrollReveal';
import useAuthStore from '../context/authStore';
import toast from 'react-hot-toast';
import './TeacherProfilePage.css';

/* ─── Share Modal ─── */
function ShareModal({ teacher, onClose }) {
  const { userDoc } = useAuthStore();
  const refCode = userDoc?.role === 'student' ? `?ref=${userDoc.referralCode}` : '';
  const profileUrl = `${window.location.origin}/teachers/${teacher.username || teacher.uid || teacher.id}${refCode}`;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      toast.success('تم نسخ الرابط!');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="share-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="share-modal">
        <div className="share-modal-header">
          <h3>مشاركة بروفايل {teacher.name}</h3>
          <button className="share-close" onClick={onClose}>✕</button>
        </div>
        <div className="share-url-row">
          <input
            className="form-input"
            value={profileUrl}
            readOnly
            style={{ fontSize: 13, direction: 'ltr', textAlign: 'left' }}
          />
          <button className="btn btn-primary btn-sm" onClick={copy}>
            {copied ? '✓ تم' : 'نسخ'}
          </button>
        </div>
        <p className="share-desc">شارك رابط البروفايل عبر:</p>
        <div className="share-platforms">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`📚 تابع المدرّس ${teacher.name} على مدرسك:\n${profileUrl}`)}`}
            target="_blank" rel="noreferrer" className="share-btn share-wa"
          >
            <span>💬</span><span>واتساب</span>
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`}
            target="_blank" rel="noreferrer" className="share-btn share-fb"
          >
            <span>📘</span><span>فيسبوك</span>
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(profileUrl)}&text=${encodeURIComponent(`تابع المدرّس ${teacher.name} على مدرسك 📚`)}`}
            target="_blank" rel="noreferrer" className="share-btn share-tw"
          >
            <span>🐦</span><span>تويتر</span>
          </a>
          <button className="share-btn share-copy" onClick={copy}>
            <span>🔗</span><span>نسخ الرابط</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function TeacherProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const pageRef = useScrollReveal();

  const [teacher, setTeacher]     = useState(null);
  const [courses, setCourses]     = useState([]);
  const [reels, setReels]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('courses');
  const [showShare, setShowShare] = useState(false);
  const [error, setError]         = useState(null);
  const { userDoc, refreshUserDoc } = useAuthStore();
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Fetch teacher + courses in parallel
    // Reels fetched separately and silently (won't break page if it fails)
    Promise.all([
      getTeacher(id),
      getCourses({ teacherId: id }),
    ])
      .then(([tr, cr]) => {
        setTeacher(tr.data);
        const published = (cr.data.courses || []).filter(
          c => c.isPublished === true || c.isPublished === 'true'
        );
        setCourses(published);
      })
      .catch(err => {
        console.error('Teacher profile error:', err);
        setError(err.response?.data?.error || 'حدث خطأ في تحميل البيانات');
      })
      .finally(() => setLoading(false));

    // Fetch reels independently — won't block the page
    api.get('/reels', { params: { teacherId: id } })
      .then(r => setReels(r.data || []))
      .catch(() => setReels([]));  // silent fail

  }, [id]);

  const stars = r => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const subjectImages = teacher?.subjectImages || {};
  const isOwn = user?.uid === teacher?.uid;

  /* Loading */
  if (loading) return (
    <div className="teacher-profile-page">
      <div className="tp-banner-wrap">
        <Skeleton width="100%" height="240px" borderRadius="0" />
      </div>
      <div className="tp-hero">
        <div className="tp-hero-inner">
          <Skeleton width="120px" height="120px" borderRadius="50%" className="tp-avatar-wrap" />
          <div className="tp-info" style={{ flex: 1 }}>
            <Skeleton width="40%" height="32px" className="mb-12" />
            <Skeleton width="20%" height="20px" className="mb-12" />
            <div style={{ display: 'flex', gap: '8px' }}>
              <Skeleton width="60px" height="24px" borderRadius="20px" />
              <Skeleton width="60px" height="24px" borderRadius="20px" />
            </div>
          </div>
        </div>
      </div>
      <div className="tp-body" style={{ padding: '40px' }}>
        <div className="tp-quick-info" style={{ marginBottom: '32px' }}>
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} width="160px" height="80px" borderRadius="12px" />)}
        </div>
        <Skeleton width="100%" height="150px" borderRadius="12px" />
      </div>
    </div>
  );

  /* Error (network / server) */
  if (error) return (
    <div className="tp-not-found">
      <div style={{ fontSize: 52 }}>⚠️</div>
      <h2>حدث خطأ في تحميل البيانات</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>{error}</p>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>إعادة المحاولة</button>
        <button className="btn btn-outline" onClick={() => navigate('/teachers')}>← عودة للمدرسين</button>
      </div>
    </div>
  );

  /* Not found */
  if (!teacher) return (
    <div className="tp-not-found">
      <div style={{ fontSize: 60 }}>🔍</div>
      <h2>المدرّس غير موجود</h2>
      <button className="btn btn-outline" onClick={() => navigate('/teachers')}>← عودة للمدرسين</button>
    </div>
  );

  const TABS = [
    { key: 'courses',  label: `📚 الكورسات (${courses.length})` },
    ...(reels.length > 0 ? [{ key: 'reels', label: `🎬 الريلز (${reels.length})` }] : []),
    ...(Object.keys(subjectImages).length > 0 ? [{ key: 'subjects', label: '📖 المواد' }] : []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="teacher-profile-page page-enter"
      ref={pageRef}
    >

      {/* ── Banner ── */}
      <div className="tp-banner-wrap">
        {teacher.banner
          ? <img src={teacher.banner} alt="" className="tp-banner-img" />
          : <div className="tp-banner-ph" />
        }
        <div className="tp-banner-overlay" />
      </div>

      {/* ── Hero ── */}
      <div className="tp-hero">
        <div className="tp-hero-inner">

          {/* Avatar */}
          <div className="tp-avatar-wrap">
            <div className="tp-avatar">
              {teacher.avatar
                ? <img src={teacher.avatar} alt={teacher.name} className="tp-avatar-img" />
                : <span className="tp-avatar-letter">{teacher.name?.[0]}</span>
              }
            </div>
            {teacher.isApproved && <div className="tp-verified">✓</div>}
          </div>

          {/* Info */}
          <div className="tp-info">
            <h1>{teacher.name}</h1>

            {teacher.rating > 0 && (
              <div className="tp-rating">
                <span className="stars">{stars(teacher.rating)}</span>
                <span className="tp-rating-num">{teacher.rating.toFixed(1)}</span>
                <span className="tp-rating-count">({teacher.totalReviews || 0} تقييم)</span>
              </div>
            )}

            {teacher.subjects?.length > 0 && (
              <div className="tp-subjects">
                {teacher.subjects.map(s => (
                  <span key={s} className="tp-subject-tag">{s}</span>
                ))}
              </div>
            )}

            {teacher.bio && (
              <p className="tp-bio-inline">{teacher.bio}</p>
            )}

            {/* Action buttons */}
            <div className="tp-hero-actions">
              <button className="btn-share" onClick={() => setShowShare(true)}>
                ↗ مشاركة البروفايل
              </button>
              {user && !isOwn && (
                <button
                  className={`btn-fav ${userDoc?.favoriteTeachers?.includes(id) ? 'active' : ''}`}
                  onClick={async () => {
                    setFavLoading(true);
                    try {
                      await toggleFavoriteTeacher(id);
                      await refreshUserDoc();
                      toast.success(userDoc?.favoriteTeachers?.includes(id) ? 'تمت الإزالة من المفضلة' : 'تمت الإضافة للمفضلة ❤️');
                    } catch { toast.error('حدث خطأ'); }
                    setFavLoading(false);
                  }}
                  disabled={favLoading}
                >
                  {userDoc?.favoriteTeachers?.includes(id) ? '❤️ المفضل' : '🤍 أضف للمفضلة'}
                </button>
              )}
              {isOwn && (
                <Link to="/dashboard" className="btn-outline-white">✏️ تعديل البروفايل</Link>
              )}
              {courses.length > 0 && (
                <a href="#tp-courses" className="btn btn-primary">📚 عرض الكورسات</a>
              )}
            </div>

          {/* Social Media Links */}
          {teacher.socialMedia && Object.values(teacher.socialMedia).some(v => v) && (
            <div className="tp-socials" style={{ marginTop: 20, display: 'flex', gap: 12 }}>
              {teacher.socialMedia.facebook && (
                <a href={teacher.socialMedia.facebook} target="_blank" rel="noreferrer" title="Facebook" className="tp-social-icon fb">
                  <span style={{ fontSize: 24 }}>📘</span>
                </a>
              )}
              {teacher.socialMedia.instagram && (
                <a href={teacher.socialMedia.instagram} target="_blank" rel="noreferrer" title="Instagram" className="tp-social-icon ig">
                  <span style={{ fontSize: 24 }}>📸</span>
                </a>
              )}
              {teacher.socialMedia.youtube && (
                <a href={teacher.socialMedia.youtube} target="_blank" rel="noreferrer" title="YouTube" className="tp-social-icon yt">
                  <span style={{ fontSize: 24 }}>📺</span>
                </a>
              )}
              {teacher.socialMedia.tiktok && (
                <a href={teacher.socialMedia.tiktok} target="_blank" rel="noreferrer" title="TikTok" className="tp-social-icon tt">
                  <span style={{ fontSize: 24 }}>🎵</span>
                </a>
              )}
              {teacher.socialMedia.linkedin && (
                <a href={teacher.socialMedia.linkedin} target="_blank" rel="noreferrer" title="LinkedIn" className="tp-social-icon li">
                  <span style={{ fontSize: 24 }}>🔗</span>
                </a>
              )}
            </div>
          )}
          </div>

          {/* Stats */}
          <div className="tp-stats">
            <div className="tp-stat">
              <span className="tp-stat-num">{courses.length}</span>
              <span className="tp-stat-lbl">كورس</span>
            </div>
            {reels.length > 0 && (
              <>
                <div className="tp-stat-div" />
                <div className="tp-stat">
                  <span className="tp-stat-num">{reels.length}</span>
                  <span className="tp-stat-lbl">ريل</span>
                </div>
              </>
            )}
            <div className="tp-stat-div" />
            <div className="tp-stat">
              <span className="tp-stat-num">{teacher.totalReviews || 0}</span>
              <span className="tp-stat-lbl">تقييم</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="tp-body">

        {/* Quick info row */}
        <div className="tp-quick-info">
          {courses.length > 0 && (
            <div className="tp-quick-card">
              <span className="tp-quick-icon">📚</span>
              <div><div className="tp-quick-num">{courses.length}</div><div className="tp-quick-lbl">كورس منشور</div></div>
            </div>
          )}
          {teacher.totalEnrolled > 0 && (
            <div className="tp-quick-card">
              <span className="tp-quick-icon">👥</span>
              <div><div className="tp-quick-num">{teacher.totalEnrolled}</div><div className="tp-quick-lbl">طالب مسجّل</div></div>
            </div>
          )}
          {teacher.rating > 0 && (
            <div className="tp-quick-card">
              <span className="tp-quick-icon">⭐</span>
              <div><div className="tp-quick-num">{teacher.rating.toFixed(1)}</div><div className="tp-quick-lbl">التقييم</div></div>
            </div>
          )}
          {teacher.pricePerHour > 0 && (
            <div className="tp-quick-card">
              <span className="tp-quick-icon">💰</span>
              <div><div className="tp-quick-num">{teacher.pricePerHour} ج.م</div><div className="tp-quick-lbl">سعر الساعة</div></div>
            </div>
          )}
          {reels.length > 0 && (
            <div className="tp-quick-card">
              <span className="tp-quick-icon">🎬</span>
              <div><div className="tp-quick-num">{reels.length}</div><div className="tp-quick-lbl">ريل تعليمي</div></div>
            </div>
          )}
        </div>

        {/* Bio */}
        {teacher.bio && (
          <div className="tp-bio-card card" data-reveal="up">
            <h2 className="tp-section-title">📝 عن المدرّس</h2>
            <p>{teacher.bio}</p>
          </div>
        )}

        {/* Tabs */}
        {TABS.length > 0 && (
          <div className="tp-tabs" id="tp-courses">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`tp-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >{t.label}</button>
            ))}
          </div>
        )}

        {/* Courses */}
        {(tab === 'courses' || TABS.length === 0) && (
          <div data-reveal="up">
            {courses.length === 0 ? (
              <div className="tp-empty">
                <span>📭</span>
                <p>لا توجد كورسات منشورة بعد</p>
                {isOwn && (
                  <Link to="/dashboard" className="btn btn-primary btn-sm" style={{ marginTop: 10 }}>
                    + إنشاء كورس جديد
                  </Link>
                )}
              </div>
            ) : (
              <div className="tp-courses-grid">
                {courses.map((c, i) => (
                  <div key={c.id} className="course-card-anim" style={{ animationDelay: `${i * 60}ms` }}>
                    <CourseCard course={c} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reels */}
        {tab === 'reels' && reels.length > 0 && (
          <div data-reveal="up">
            <div className="tp-reels-grid">
              {reels.map(r => (
                <Link key={r.id} to="/reels" className="reel-thumb-card">
                  <div
                    className="reel-thumb-img"
                    style={{ backgroundImage: r.thumbnailUrl ? `url(${r.thumbnailUrl})` : undefined }}
                  >
                    <div className="reel-thumb-play">▶</div>
                  </div>
                  <p className="reel-thumb-title">{r.title}</p>
                  <span className="reel-thumb-subject">{r.subject}</span>
                </Link>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Link to="/reels" className="btn btn-primary">🎬 شوف كل الريلز</Link>
            </div>
          </div>
        )}

        {/* Subjects */}
        {tab === 'subjects' && Object.keys(subjectImages).length > 0 && (
          <div className="tp-subjects-grid" data-reveal="up" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 }}>
            {Object.entries(subjectImages).map(([subj, img]) => (
              <div key={subj} className="tp-subject-card card" style={{ padding: 0, overflow: 'hidden', textAlign: 'center' }}>
                <div style={{ width: '100%', height: 140, background: 'var(--blue-light)', overflow: 'hidden' }}>
                  {img
                    ? <img src={img} alt={subj} className="tp-subject-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 50 }}>📖</div>
                  }
                </div>
                <div style={{ padding: 12, fontWeight: 700 }}>{subj}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share modal */}
      {showShare && <ShareModal teacher={teacher} onClose={() => setShowShare(false)} />}
    </motion.div>
  );
}
